import { isPlatformServer } from '@angular/common';
import { Router } from '@angular/router';
import { Auth } from '@freelancer/auth';
import { FreelancerHttp } from '@freelancer/freelancer-http';
import { Location } from '@freelancer/location';
import { Tracking } from '@freelancer/tracking';
import { CookieService } from '@laurentgoudet/ngx-cookie';
import * as md5 from 'blueimp-md5';
import * as Rx from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import * as i0 from "@angular/core";
import * as i1 from "../auth/auth.service";
import * as i2 from "@laurentgoudet/ngx-cookie";
import * as i3 from "../freelancer-http/freelancer-http.service";
import * as i4 from "../tracking/tracking.service";
import * as i5 from "../location/location.service";
import * as i6 from "@angular/router";
import * as i7 from "./abtest.config";
import * as i8 from "@nguniversal/express-engine/tokens";
export var ABTestType;
(function (ABTestType) {
    ABTestType[ABTestType["SESSION_BASED"] = 1] = "SESSION_BASED";
    ABTestType[ABTestType["USER_BASED"] = 2] = "USER_BASED";
    ABTestType[ABTestType["PROJECT_BASED"] = 3] = "PROJECT_BASED";
})(ABTestType || (ABTestType = {}));
export var STORAGE_KEY = 'WEBAPP_ABTEST_CACHE_V1';
// FIXME(T66231): we should make the A/B test service return a tuple of
// `response | error` instead of throwing exceptions, as handling of these
// later can't be enforced in TypeScript.
//
// For now, make sure you catch any ABTest service failure or bad things will
// happen when the backend is down.
var ABTest = /** @class */ (function () {
    function ABTest(auth, cookies, freelancerHttp, tracking, location, router, whitelistCookie, platformId, request, response) {
        this.auth = auth;
        this.cookies = cookies;
        this.freelancerHttp = freelancerHttp;
        this.tracking = tracking;
        this.location = location;
        this.router = router;
        this.whitelistCookie = whitelistCookie;
        this.platformId = platformId;
        this.request = request;
        this.response = response;
    }
    ABTest.prototype.getSessionExperimentVariation = function (experimentName) {
        var _this = this;
        return this.tracking.getSessionId().pipe(take(1), switchMap(function (sessionId) {
            return _this.getVariation(ABTestType.SESSION_BASED, experimentName, sessionId);
        }));
    };
    // userId is optional, as it'll default to the user's own id
    ABTest.prototype.getUserExperimentVariation = function (experimentName, userId) {
        var _this = this;
        var userId$ = userId
            ? Rx.of(userId)
            : this.auth.getUserId().pipe(map(function (uid) { return parseInt(uid, 10); }));
        return userId$.pipe(take(1), switchMap(function (uid) {
            return _this.getVariation(ABTestType.USER_BASED, experimentName, uid);
        }));
    };
    ABTest.prototype.getProjectExperimentVariation = function (experimentName, projectId) {
        return this.getVariation(ABTestType.PROJECT_BASED, experimentName, projectId);
    };
    /**
     * Determines whether or not to enrol a user/project based on a percentage split.
     * Use this function instead of MOD if you want to gradually increase enrolment on an experiment.
     * Enrol/activate the test separately using the appropriate `get*Variation`. function
     *
     * Functionally similar to ABTest::md5sumTestHelper in ABTest.php
     *
     * @param experimentName name of the experiment
     * @param contextId id of user (for user-based tests) or project (for project-based tests) being split on
     * @param testProportion proportion to allow enrolment (eg. 0.2)
     * @returns `true` if the test should be enrolled, false if not
     */
    ABTest.prototype.shouldEnrol = function (experimentName, contextId, testProportion) {
        if (isPlatformServer(this.platformId)) {
            throw new Error('getVariation() cannot be ran on the server, you must explicitly call ABTest::handleServer() first');
        }
        // always enroll when an override is defined as otheriwise it would break
        // the test overrides logic
        var override = this.getOverride(experimentName);
        if (override) {
            return true;
        }
        var hash = md5("" + contextId + experimentName)
            .toString()
            .substr(0, 8);
        var quotient = parseInt(hash, 16) / 0xffffffff;
        return quotient < testProportion;
    };
    ABTest.prototype.isWhitelistUser = function () {
        if (isPlatformServer(this.platformId)) {
            throw new Error('isWhitelistUser() cannot be ran on the server, you must explicitly call ABTest::handleServer() first');
        }
        return this.cookies.get(this.whitelistCookie) === 'true';
    };
    /**
     * Handles A/B tests on the server-side, as none of the other A/B test method
     * will work on the server: this must be called on the server-side only,
     * before any method ABTest method call, by guarding it with
     * `isPlatformServer` check, i.e.:
     *
     *
     * if (isPlatformServer(this.platformId)) {
     *   return this.abTest.handleServer.then(() =>
     *     this.redirectToPhp.doRedirect(fallbackUrl),
     *   );
     * }
     *
     * The `then` callback is called when the default variant should be shown
     * (e.g. UA is a Bot). Otherwise, it will render a blank overlay & will defer
     * the A/B test code evaluation to the client-side.
     */
    ABTest.prototype.handleServer = function () {
        this.response.append('Vary', 'X-User-Agent-Bot');
        if (this.request.get('X-User-Agent-Bot') === 'true') {
            return Promise.resolve(undefined);
        }
        return this.router
            .navigate(['/internal/blank'], {
            skipLocationChange: true,
        })
            .then(function () {
            return new Promise(function (resolve, reject) {
                // wait forever
            });
        });
    };
    ABTest.prototype.getVariation = function (experimentType, experimentName, identifier) {
        if (isPlatformServer(this.platformId)) {
            throw new Error('getVariation() cannot be ran on the server, you must explicitly call ABTest::handleServer() first');
        }
        // global test disabling flag, used by automation tools
        if (this.cookies.get('no_abtest') !== undefined) {
            return Rx.of(undefined);
        }
        // test overrides functionality
        var override = this.getOverride(experimentName);
        if (override) {
            return Rx.of(override);
        }
        // try reading the variation from cache if it exists
        var cacheKey = STORAGE_KEY + "|" + experimentType + "|" + experimentName + "|" + identifier;
        var variationFromStorage;
        try {
            variationFromStorage =
                window.sessionStorage.getItem(cacheKey) || '';
        }
        catch (e) {
            // ignore the errors, e.g. quota is full or security error
            console.error(e);
        }
        if (variationFromStorage) {
            return Rx.of(variationFromStorage);
        }
        // overwise get the variation for the A/B test server
        return this.freelancerHttp
            .post('abtest/0.1/enrollments/', {
            id_type: experimentType,
            experiment_name: experimentName,
            id: identifier,
        })
            .pipe(map(function (response) {
            if (response.status !== 'success') {
                return undefined;
            }
            return response.result.variation_name;
        }), tap(function (variationName) {
            if (variationName !== undefined) {
                try {
                    window.sessionStorage.setItem(cacheKey, variationName);
                }
                catch (e) {
                    // ignore the errors, e.g. quota is full or security error
                    console.error(e);
                }
            }
        }), catchError(function () { return Rx.of(undefined); }));
    };
    /*
     * This allows to override A/B tests using the
     * ?overrides=<test_name>:<test_variant> query param.
     * Overrides can be chained to override multiple tests, e.g.
     * ?overrides=<test1_name>:<test1_variant>,<test1_name>:<test1_variant>.
     */
    ABTest.prototype.getOverride = function (experimentName) {
        var overrides = new URL(this.location.href).searchParams.get('overrides');
        if (overrides) {
            var overridesMap = overrides
                .split(',')
                .reduce(function (acc, experiment) {
                var _a = experiment.split(':'), name = _a[0], variation = _a[1];
                acc[name] = variation;
                return acc;
            }, {});
            if (overridesMap[experimentName]) {
                return overridesMap[experimentName];
            }
        }
    };
    ABTest.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function ABTest_Factory() { return new ABTest(i0.ɵɵinject(i1.Auth), i0.ɵɵinject(i2.CookieService), i0.ɵɵinject(i3.FreelancerHttp), i0.ɵɵinject(i4.Tracking), i0.ɵɵinject(i5.Location), i0.ɵɵinject(i6.Router), i0.ɵɵinject(i7.ABTEST_WHITELIST_COOKIE), i0.ɵɵinject(i0.PLATFORM_ID), i0.ɵɵinject(i8.REQUEST, 8), i0.ɵɵinject(i8.RESPONSE, 8)); }, token: ABTest, providedIn: "root" });
    return ABTest;
}());
export { ABTest };
